import React, { JSX, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import withForm from '../../common/withForm';
import unpairForm, { UnpairInput } from './unpairForm';
import pairingForm, { PairingInput } from './pairingForm';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CustomModal from '../../common/withModal';
import PairingHistory from './pairingHistory';
import { PairingInfo } from '../../../types/PairingInfo';
import AuthService from '../../../services/auth.service';
import { AppPermissions } from '../../../constants/Permissions';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

interface PairingInfoDetailsProps {
  historyNeedsUpdate: boolean;
  pairingInfo?: PairingInfo;
  noHistoryCardRadius?: boolean;
  // if true, we're looking at the pairing from the perspective of a device.
  // Otherwise, we're looking from the perspective of equipment
  isFromDevicePerspective: boolean;
  pair: (deviceId: string, equipmentId: string) => Promise<void>;
  unpair: (values: any) => Promise<void>;
}

export default function PairingInfoDetails(props: PairingInfoDetailsProps): JSX.Element {
  const [unpairingFormIsOpen, setUnpairingFormIsOpen] = useState(false);
  const [pairingFormIsOpen, setPairingFormIsOpen] = useState(false);
  const [currentPairingOption, setCurrentPairingOption] = useState({ label: '', id: '' });

  const { pairingInfo, isFromDevicePerspective } = props;

  const determineCanPairDevice = () => {
    return (
      isFromDevicePerspective &&
      (pairingInfo?.isDevicePairable ||
        (AuthService.hasPermission(AppPermissions.Pairing.OverrideHealthCheck) && pairingInfo?.isDevicePairableWithoutHealthCheck))
    );
  };

  const determineCanPairEquipment = () => {
    return !isFromDevicePerspective && pairingInfo?.isEquipmentPairable;
  };

  // check if the user can pair the current object
  const canPairCurrentObject = determineCanPairDevice() || determineCanPairEquipment();

  const unpairFormSetup = useFormik<UnpairInput>({
    initialValues: {
      unpairReasons: pairingInfo?.applicableUnpairingReasons.filter((x) => x.isSelected).map((x) => x.reason) ?? [],
    },
    validationSchema: () => {
      return Yup.object<UnpairInput>({
        unpairReasons: Yup.array().of(Yup.number()).min(1, 'At least one reason is required'),
      });
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      props.unpair(values);
      setUnpairingFormIsOpen(false);
    },
  });

  const UnPairForm = withForm(unpairForm);

  const pairingFormSetup = useFormik<PairingInput>({
    initialValues: {
      serialNumber: '',
    },
    validationSchema: () => {
      return Yup.object<PairingInput>({
        // serialNumber: Yup.string().required('Serial number is required'),
      });
    },
    enableReinitialize: true, // Enable reinitialization
    onSubmit: async () => {
      const deviceId = props.isFromDevicePerspective ? pairingInfo?.device.deviceId : currentPairingOption.id;
      const equipmentId = props.isFromDevicePerspective ? currentPairingOption.id : pairingInfo?.equipment.equipmentId;

      if (deviceId && equipmentId) {
        await props.pair(deviceId, equipmentId);
      }

      setPairingFormIsOpen(false);
    },
  });

  const PairingForm = withForm(pairingForm);

  const handleUnpairClick = () => {
    unpairFormSetup.resetForm();
    setUnpairingFormIsOpen(true);
  };

  const handlePairClick = () => {
    pairingFormSetup.resetForm();
    setCurrentPairingOption({ id: '', label: '' });
    setPairingFormIsOpen(true);
  };

  const history = (
    <>
      <PairingHistory
        deviceId={isFromDevicePerspective ? pairingInfo?.device?.deviceId : undefined}
        equipmentId={isFromDevicePerspective ? undefined : pairingInfo?.equipment?.id}
        needsUpdate={props.historyNeedsUpdate}
        noCardRadius={props.noHistoryCardRadius}
      />
    </>
  );

  if (!pairingInfo?.currentPairing) {
    const button = canPairCurrentObject ? (
      <Button onClick={handlePairClick} variant='contained' size='small' sx={tablePrimaryButtonSx}>
        Pair
      </Button>
    ) : (
      <Typography variant='body2' color='text.secondary'>
        This item cannot be paired
      </Typography>
    );

    return (
      <>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent='space-between'
          spacing={1.25}
          sx={{ mb: 1.5 }}
        >
          <Typography variant='body1' sx={{ fontWeight: 600 }}>
            Not currently paired
          </Typography>
          {button}
        </Stack>
        {history}
        <CustomModal
          maxWidth='lg'
          title='Pair'
          isOpen={pairingFormIsOpen}
          isValid={pairingFormSetup.isValid}
          setIsOpen={setPairingFormIsOpen}
          onConfirm={pairingFormSetup.handleSubmit}
        >
          <PairingForm
            currentValue={currentPairingOption}
            setCurrentValue={setCurrentPairingOption}
            isFromDevicePerspective={isFromDevicePerspective}
            formRef={pairingFormSetup}
          />
        </CustomModal>
      </>
    );
  }

  // Show pairing information and possibility to unpair when paired
  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent='space-between'
        spacing={1.25}
        sx={{ mb: 1.5, p: { xs: 0.75, sm: 1.25 } }}
      >
        <Typography variant='body1' sx={{ fontWeight: 600 }}>
          Paired to {isFromDevicePerspective ? pairingInfo?.equipment?.serialNumber : pairingInfo?.device?.deviceId}
        </Typography>
        <Button onClick={handleUnpairClick} variant='outlined' color='error' size='small' sx={{ textTransform: 'none', borderRadius: '12px', px: 2 }}>
          Unpair
        </Button>
      </Stack>
      {history}
      <CustomModal
        title='Unpair - Select reasons'
        isOpen={unpairingFormIsOpen}
        isValid={unpairFormSetup.isValid}
        setIsOpen={setUnpairingFormIsOpen}
        onConfirm={unpairFormSetup.handleSubmit}
      >
        <UnPairForm unpairingReasons={pairingInfo.applicableUnpairingReasons} formRef={unpairFormSetup} />
      </CustomModal>
    </>
  );
}
